import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CashFlowTransaction, CashFlowStats } from '../api/transaction-api';
import type { Invoice, InvoiceStats } from '../api/invoice-api';
import type { Commission, CommissionStats } from '../api/commissions-api';

// ============================================================================
// REQUEST TYPES
// ============================================================================

export interface GetReceivablesRequest {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
}

export interface GetCashFlowRequest {
    page?: number;
    limit?: number;
    search?: string;
    type?: 'income' | 'expense';
    startDate?: string;
    endDate?: string;
}

export interface GetCommissionsRequest {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    agentId?: string;
    startDate?: string;   // Filtro de fecha inicio
    endDate?: string;     // Filtro de fecha fin
    minAmount?: number;   // Filtro de monto mínimo
    maxAmount?: number;   // Filtro de monto máximo
}

export interface CreateCashFlowTransactionRequest {
    type: 'income' | 'expense';
    category: string;
    description: string;
    amount: number;
    currency?: string;
    date: string;
    reference?: string;
    paymentMethod?: string;
    notes?: string;
}

export interface UpdateCashFlowTransactionRequest {
    id: string;
    data: Partial<CreateCashFlowTransactionRequest>;
}

export interface DeleteCashFlowTransactionRequest {
    id: string;
}

export interface CreateReceivableRequest {
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    type: 'sale' | 'rental' | 'service' | 'commission' | 'other';
    description?: string;
    amount?: number; // Optional if using items
    notes?: string;
    issueDate: string;
    dueDate?: string;
    items?: Array<{
        description: string;
        quantity: number;
        unitPrice: number;
        taxRate?: number;
    }>;
    currency: string;
}

// ============================================================================
// STATE TYPES
// ============================================================================

export interface ErpState {
    // Receivables (Cuentas por Cobrar)
    receivables: {
        data: Invoice[];
        stats: InvoiceStats | null;
        loading: boolean;
        error: string | null;
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    };

    // Cash Flow (Flujo de Caja)
    cashFlow: {
        data: CashFlowTransaction[];
        stats: CashFlowStats | null;
        loading: boolean;
        error: string | null;
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    };

    // Commissions (Comisiones)
    commissions: {
        data: Commission[];
        stats: CommissionStats | null;
        loading: boolean;
        error: string | null;
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    };

    // Income Form (Formulario de Registro de Ingresos)
    incomeForm: {
        data: CreateReceivableRequest | null;
        loading: boolean;
        error: string | null;
        step: number; // Current step (0-3)
        validationErrors: Record<string, string>;
    };
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ErpState = {
    receivables: {
        data: [],
        stats: null,
        loading: false,
        error: null,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
        },
    },
    cashFlow: {
        data: [],
        stats: null,
        loading: false,
        error: null,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
        },
    },
    commissions: {
        data: [],
        stats: null,
        loading: false,
        error: null,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
        },
    },
    incomeForm: {
        data: null,
        loading: false,
        error: null,
        step: 0,
        validationErrors: {},
    },
};

// ============================================================================
// SLICE
// ============================================================================

const erpSlice = createSlice({
    name: 'erp',
    initialState,
    reducers: {
        // ========================================================================
        // RECEIVABLES ACTIONS
        // ========================================================================

        getReceivablesRequest: (state, _action: PayloadAction<GetReceivablesRequest>) => {
            state.receivables.loading = true;
            state.receivables.error = null;
        },

        getReceivablesSuccess: (state, action: PayloadAction<{ data: Invoice[]; total: number; page: number; limit: number; totalPages: number }>) => {
            state.receivables.loading = false;
            state.receivables.data = action.payload.data;
            state.receivables.pagination = {
                page: action.payload.page,
                limit: action.payload.limit,
                total: action.payload.total,
                totalPages: action.payload.totalPages,
            };
            state.receivables.error = null;
        },

        getReceivablesStatsRequest: (state) => {
            state.receivables.loading = true;
            state.receivables.error = null;
        },

        getReceivablesStatsSuccess: (state, action: PayloadAction<InvoiceStats>) => {
            state.receivables.stats = action.payload;
            state.receivables.loading = false;
            state.receivables.error = null;
        },

        createReceivableRequest: (state, _action: PayloadAction<CreateReceivableRequest>) => {
            state.receivables.loading = true;
            state.receivables.error = null;
        },

        createReceivableSuccess: (state, action: PayloadAction<Invoice>) => {
            state.receivables.loading = false;
            state.receivables.data = [action.payload, ...state.receivables.data];
            state.receivables.error = null;
        },

        receivablesFailure: (state, action: PayloadAction<string>) => {
            state.receivables.loading = false;
            state.receivables.error = action.payload;
        },

        // ========================================================================
        // CASH FLOW ACTIONS
        // ========================================================================

        getCashFlowRequest: (state, _action: PayloadAction<GetCashFlowRequest>) => {
            state.cashFlow.loading = true;
            state.cashFlow.error = null;
        },

        getCashFlowSuccess: (state, action: PayloadAction<{ data: CashFlowTransaction[]; total: number; page: number; limit: number; totalPages: number }>) => {
            state.cashFlow.loading = false;
            state.cashFlow.data = action.payload.data;
            state.cashFlow.pagination = {
                page: action.payload.page,
                limit: action.payload.limit,
                total: action.payload.total,
                totalPages: action.payload.totalPages,
            };
            state.cashFlow.error = null;
        },

        getCashFlowStatsRequest: (state, _action: PayloadAction<{ startDate?: string; endDate?: string }>) => {
            state.cashFlow.loading = true;
            state.cashFlow.error = null;
        },

        getCashFlowStatsSuccess: (state, action: PayloadAction<CashFlowStats>) => {
            state.cashFlow.stats = action.payload;
            state.cashFlow.loading = false;
            state.cashFlow.error = null;
        },

        createCashFlowTransactionRequest: (state, _action: PayloadAction<CreateCashFlowTransactionRequest>) => {
            state.cashFlow.loading = true;
            state.cashFlow.error = null;
        },

        createCashFlowTransactionSuccess: (state, action: PayloadAction<CashFlowTransaction>) => {
            state.cashFlow.loading = false;
            state.cashFlow.data = [action.payload, ...state.cashFlow.data];
            state.cashFlow.error = null;
        },

        updateCashFlowTransactionRequest: (state, _action: PayloadAction<UpdateCashFlowTransactionRequest>) => {
            state.cashFlow.loading = true;
            state.cashFlow.error = null;
        },

        updateCashFlowTransactionSuccess: (state, action: PayloadAction<CashFlowTransaction>) => {
            state.cashFlow.loading = false;
            const index = state.cashFlow.data.findIndex(t => t.id === action.payload.id);
            if (index !== -1) {
                state.cashFlow.data[index] = action.payload;
            }
            state.cashFlow.error = null;
        },

        deleteCashFlowTransactionRequest: (state, _action: PayloadAction<DeleteCashFlowTransactionRequest>) => {
            state.cashFlow.loading = true;
            state.cashFlow.error = null;
        },

        deleteCashFlowTransactionSuccess: (state, action: PayloadAction<string>) => {
            state.cashFlow.loading = false;
            state.cashFlow.data = state.cashFlow.data.filter(t => t.id !== action.payload);
            state.cashFlow.error = null;
        },

        cashFlowFailure: (state, action: PayloadAction<string>) => {
            state.cashFlow.loading = false;
            state.cashFlow.error = action.payload;
        },

        // ========================================================================
        // COMMISSIONS ACTIONS
        // ========================================================================

        getCommissionsRequest: (state, _action: PayloadAction<GetCommissionsRequest>) => {
            state.commissions.loading = true;
            state.commissions.error = null;
        },

        getCommissionsSuccess: (state, action: PayloadAction<{ data: Commission[]; total: number; page: number; limit: number; totalPages: number }>) => {
            state.commissions.loading = false;
            state.commissions.data = action.payload.data;
            state.commissions.pagination = {
                page: action.payload.page,
                limit: action.payload.limit,
                total: action.payload.total,
                totalPages: action.payload.totalPages,
            };
            state.commissions.error = null;
        },

        getCommissionsStatsRequest: (state) => {
            state.commissions.loading = true;
            state.commissions.error = null;
        },

        getCommissionsStatsSuccess: (state, action: PayloadAction<CommissionStats>) => {
            state.commissions.stats = action.payload;
            state.commissions.loading = false;
            state.commissions.error = null;
        },

        updateCommissionRequest: (state, _action: PayloadAction<{ id: string; data: any }>) => {
            state.commissions.loading = true;
            state.commissions.error = null;
        },

        updateCommissionSuccess: (state, action: PayloadAction<Commission>) => {
            state.commissions.loading = false;
            const index = state.commissions.data.findIndex(c => c.id === action.payload.id);
            if (index !== -1) {
                state.commissions.data[index] = action.payload;
            }
            state.commissions.error = null;
        },

        updateCommissionFailure: (state, action: PayloadAction<string>) => {
            state.commissions.loading = false;
            state.commissions.error = action.payload;
        },

        commissionsFailure: (state, action: PayloadAction<string>) => {
            state.commissions.loading = false;
            state.commissions.error = action.payload;
        },

        // ========================================================================
        // INCOME FORM ACTIONS
        // ========================================================================

        setIncomeFormStep: (state, action: PayloadAction<number>) => {
            state.incomeForm.step = action.payload;
        },

        setIncomeFormData: (state, action: PayloadAction<Partial<CreateReceivableRequest>>) => {
            state.incomeForm.data = {
                ...state.incomeForm.data,
                ...action.payload,
            } as CreateReceivableRequest;
        },

        clearIncomeForm: (state) => {
            state.incomeForm.data = null;
            state.incomeForm.loading = false;
            state.incomeForm.error = null;
            state.incomeForm.step = 0;
            state.incomeForm.validationErrors = {};
        },

        submitIncomeFormRequest: (state, _action: PayloadAction<CreateReceivableRequest>) => {
            state.incomeForm.loading = true;
            state.incomeForm.error = null;
        },

        submitIncomeFormSuccess: (state, _action: PayloadAction<Invoice>) => {
            state.incomeForm.loading = false;
            state.incomeForm.error = null;
        },

        submitIncomeFormFailure: (state, action: PayloadAction<string>) => {
            state.incomeForm.loading = false;
            state.incomeForm.error = action.payload;
        },

        setIncomeFormValidationErrors: (state, action: PayloadAction<Record<string, string>>) => {
            state.incomeForm.validationErrors = action.payload;
        },

        // ========================================================================
        // UTILITY ACTIONS
        // ========================================================================

        clearErpErrors: (state) => {
            state.receivables.error = null;
            state.cashFlow.error = null;
            state.commissions.error = null;
        },

        clearErpData: (state) => {
            state.receivables.data = [];
            state.receivables.stats = null;
            state.cashFlow.data = [];
            state.cashFlow.stats = null;
            state.commissions.data = [];
            state.commissions.stats = null;
        },
    },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
    // Receivables
    getReceivablesRequest,
    getReceivablesSuccess,
    getReceivablesStatsRequest,
    getReceivablesStatsSuccess,
    createReceivableRequest,
    createReceivableSuccess,
    receivablesFailure,

    // Cash Flow
    getCashFlowRequest,
    getCashFlowSuccess,
    getCashFlowStatsRequest,
    getCashFlowStatsSuccess,
    createCashFlowTransactionRequest,
    createCashFlowTransactionSuccess,
    updateCashFlowTransactionRequest,
    updateCashFlowTransactionSuccess,
    deleteCashFlowTransactionRequest,
    deleteCashFlowTransactionSuccess,
    cashFlowFailure,

    // Commissions
    getCommissionsRequest,
    getCommissionsSuccess,
    getCommissionsStatsRequest,
    getCommissionsStatsSuccess,
    updateCommissionRequest,
    updateCommissionSuccess,
    updateCommissionFailure,
    commissionsFailure,

    // Income Form
    setIncomeFormStep,
    setIncomeFormData,
    clearIncomeForm,
    submitIncomeFormRequest,
    submitIncomeFormSuccess,
    submitIncomeFormFailure,
    setIncomeFormValidationErrors,

    // Utility
    clearErpErrors,
    clearErpData,
} = erpSlice.actions;

export default erpSlice.reducer;
